/**
 * Audit Dashboard (#579).
 *
 * Renders the per-tenant activity summary, recent events with filters,
 * and a CSV export action. Tenant scoping is enforced server-side from
 * the JWT — the UI never sends tenant_id.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import {
  ActivityPeriod,
  ActivityResult,
  ActivitySummary,
  AuditEventPage,
  activityService,
} from '@services/activity.service';
import { useAppSelector } from '@store/index';
import { selectUser } from '@features/auth/authSlice';
import { useTranslation } from 'react-i18next';

const PERIOD_OPTIONS: ActivityPeriod[] = ['1d', '7d', '30d', '90d'];
const RESULT_OPTIONS: Array<{ value: '' | ActivityResult }> = [
  { value: '' },
  { value: 'success' },
  { value: 'failure' },
  { value: 'error' },
];

const formatDateTime = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const StatCard: React.FC<{ label: string; value: number | string }> = ({ label, value }) => (
  <Card variant="outlined">
    <CardContent>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h4">{value}</Typography>
    </CardContent>
  </Card>
);

const AuditDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  // CSV export endpoint is admin-only server-side. Manager users can
  // reach this page for the read-only summary/event view, but rendering
  // the export button to them produces a guaranteed 403 — so we hide it
  // when the role is not admin.
  const user = useAppSelector(selectUser);
  const userRoles = React.useMemo(() => {
    const roles: string[] = [];
    if (user?.role) roles.push(user.role);
    if (Array.isArray(user?.roles)) {
      user.roles.forEach((r) => {
        if (!roles.includes(r)) roles.push(r);
      });
    }
    return roles;
  }, [user]);
  const canExport = userRoles.includes('admin');

  const [period, setPeriod] = useState<ActivityPeriod>('7d');
  const [resultFilter, setResultFilter] = useState<'' | ActivityResult>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [summary, setSummary] = useState<ActivitySummary | null>(null);
  const [page, setPage] = useState<AuditEventPage | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSummary = useCallback(async () => {
    setLoadingSummary(true);
    setError(null);
    try {
      const data = await activityService.getSummary(period);
      setSummary(data);
    } catch (e) {
      setError(t('audit.summaryFetchFailed', { message: (e as Error).message ?? e }));
    } finally {
      setLoadingSummary(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t only builds the fetch-error message; excluded so a language change doesn't refetch the summary (PR #790)
  }, [period]);

  const loadEvents = useCallback(async () => {
    setLoadingEvents(true);
    setError(null);
    try {
      const data = await activityService.listEvents({
        result: resultFilter || undefined,
        action_category: categoryFilter || undefined,
        limit: 100,
      });
      setPage(data);
    } catch (e) {
      setError(t('audit.eventsFetchFailed', { message: (e as Error).message ?? e }));
    } finally {
      setLoadingEvents(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t only builds the fetch-error message; excluded so a language change doesn't refetch events (PR #790)
  }, [resultFilter, categoryFilter]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  const onExportCsv = useCallback(async () => {
    setExporting(true);
    try {
      const blob = await activityService.exportEventsCsv({
        result: resultFilter || undefined,
        action_category: categoryFilter || undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_logs_${new Date().toISOString()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(t('audit.csvExportFailed', { message: (e as Error).message ?? e }));
    } finally {
      setExporting(false);
    }
  }, [resultFilter, categoryFilter, t]);

  const categories = useMemo(
    () => summary?.by_category ?? [],
    [summary],
  );

  return (
    <Box p={3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h4">{t('audit.title')}</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2">{t('audit.period')}:</Typography>
          <Select
            size="small"
            value={period}
            onChange={(e) => setPeriod(e.target.value as ActivityPeriod)}
          >
            {PERIOD_OPTIONS.map((p) => (
              <MenuItem key={p} value={p}>{p}</MenuItem>
            ))}
          </Select>
        </Stack>
      </Stack>

      {error && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
          {error}
        </Paper>
      )}

      {loadingSummary || !summary ? (
        <Box display="flex" justifyContent="center" my={4}><CircularProgress /></Box>
      ) : (
        <>
          <Grid container spacing={2} mb={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard label={t('audit.totalEvents')} value={summary.total_events.toLocaleString()} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard label={t('audit.activeUsers')} value={summary.unique_users.toLocaleString()} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard label={t('audit.totalAnomalies')} value={summary.anomalies.total} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard label={t('audit.unresolvedAnomalies')} value={summary.anomalies.unresolved} />
            </Grid>
          </Grid>

          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>{t('audit.eventsByCategory')}</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {categories.length === 0 && (
                <Typography variant="body2" color="text.secondary">{t('common.noData')}</Typography>
              )}
              {categories.map((c) => (
                <Paper
                  key={c.action_category}
                  variant="outlined"
                  sx={{ px: 1.5, py: 0.5, m: 0.5 }}
                >
                  <Typography variant="caption">{c.action_category}</Typography>
                  <Typography variant="body2" fontWeight="bold">{c.count}</Typography>
                </Paper>
              ))}
            </Stack>
          </Paper>
        </>
      )}

      <Paper sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
          <Typography variant="subtitle1">{t('audit.recentEvents')}</Typography>
          <Box flex={1} />
          <Select
            size="small"
            value={resultFilter}
            onChange={(e) => setResultFilter(e.target.value as '' | ActivityResult)}
          >
            {RESULT_OPTIONS.map((opt) => (
              <MenuItem key={opt.value || 'all'} value={opt.value}>
                {t(`audit.results.${opt.value || 'all'}`)}
              </MenuItem>
            ))}
          </Select>
          <Select
            size="small"
            displayEmpty
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">{t('audit.categoryAll')}</MenuItem>
            {categories.map((c) => (
              <MenuItem key={c.action_category} value={c.action_category}>
                {c.action_category}
              </MenuItem>
            ))}
          </Select>
          {canExport && (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={onExportCsv}
              disabled={exporting}
            >
              CSV
            </Button>
          )}
        </Stack>

        {loadingEvents ? (
          <Box display="flex" justifyContent="center" my={4}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('audit.time')}</TableCell>
                  <TableCell>{t('audit.action')}</TableCell>
                  <TableCell>{t('audit.result')}</TableCell>
                  <TableCell>{t('audit.user')}</TableCell>
                  <TableCell>{t('audit.resource')}</TableCell>
                  <TableCell>{t('audit.durationMs')}</TableCell>
                  <TableCell>IP</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(page?.items ?? []).map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{formatDateTime(row.created_at)}</TableCell>
                    <TableCell><code>{row.action}</code></TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{
                          color:
                            row.result === 'success' ? 'success.main' :
                            row.result === 'failure' ? 'warning.main' : 'error.main',
                        }}
                      >
                        {row.result}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.user_id?.slice(0, 8) ?? '-'}</TableCell>
                    <TableCell>
                      {row.resource_type
                        ? `${row.resource_type}${row.resource_name ? `: ${row.resource_name}` : ''}`
                        : '-'}
                    </TableCell>
                    <TableCell>{row.duration_ms ?? '-'}</TableCell>
                    <TableCell>{row.ip_address ?? '-'}</TableCell>
                  </TableRow>
                ))}
                {page && page.items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary">
                        {t('audit.noMatchingEvents')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default AuditDashboardPage;
